import { ReactNode } from 'react';

import {
  Box,
  Icons,
  Stack,
  SxProps,
  tokens,
  Tooltip,
  Typography,
} from '@cast/design-system';

import { InventoryListItem } from '../../../_components/ui/InventoryListItem/InventoryListItem';

export type DifferenceDirection = 'up' | 'down';

export type MetricLegendItem = {
  /** Stable identifier for the metric row. */
  id: string;
  /** Row label shown on the left. */
  label: string;
  /** Formatted value shown on the right. */
  value: string;
  /** Color used for the square marker. */
  markerColor: string;
  /** Whether to disable the row (reduces opacity and removes interactivity). */
  disabled?: boolean;
  /** Whether to dim the row, e.g. for an optional/disabled metric. */
  dimmed?: boolean;
  /** Click handler. When provided, the row becomes interactive. */
  onClick?: () => void;
  /** Text color of the value. */
  valueColor?: string;
  /** Additional sx applied to the value Typography. */
  valueSx?: SxProps;
  /** Tooltip content applied to the whole row. */
  rowTooltip?: ReactNode;
};

export type AutoscalingLegendProps = {
  /** `data-testid` attribute value */
  testId?: string;
  /** MUI sx prop */
  sx?: SxProps;
  requested: string;
  /** Color of the Requested marker. Defaults to blue-300. */
  requestedMarkerColor?: string;
  /** Click handler for the Requested row. */
  requestedOnClick?: () => void;
  /** Whether the Requested row is disabled. */
  requestedDisabled?: boolean;
  /** Text color of the Requested value. */
  requestedValueColor?: string;
  /** Additional sx applied to the Requested value. */
  requestedValueSx?: SxProps;
  recommended: string;
  /** Color of the Recommended marker. Defaults to grey-900. */
  recommendedMarkerColor?: string;
  /** Click handler for the Recommended row. */
  recommendedOnClick?: () => void;
  /** Whether the Recommended row is disabled. */
  recommendedDisabled?: boolean;
  /** Text color of the Recommended value. */
  recommendedValueColor?: string;
  /** Additional sx applied to the Recommended value. */
  recommendedValueSx?: SxProps;
  /** Tooltip content applied to the whole Recommended row. */
  recommendedRowTooltip?: ReactNode;
  /** Custom element rendered next to the Recommended value (e.g. a warning tooltip). */
  recommendedWarningTooltip?: ReactNode;
  /** Custom element rendered next to the Recommended value explaining a within-threshold recommendation (e.g. an info tooltip). */
  recommendedNoChangeTooltip?: ReactNode;
  /** Optional difference row (shown without a marker and with a direction caret). */
  difference?: string;
  /** Direction of the difference caret. Defaults to "down". */
  differenceDirection?: DifferenceDirection;
  /** Color used for the difference caret. Defaults to success for down, error for up. */
  differenceColor?: string;
  /** Dynamic list of metric rows rendered below the top group. */
  metrics: MetricLegendItem[];
};

type InternalLegendItem = {
  label: string;
  value: string;
  markerColor?: string;
  startContent?: ReactNode;
  endContent?: ReactNode;
  disabled?: boolean;
  dimmed?: boolean;
  onClick?: () => void;
  valueColor?: string;
  valueSx?: SxProps;
  rowTooltip?: ReactNode;
};

const MARKER_SIZE = 8;
const DISABLED_OPACITY = 0.25;
const DIMMED_OPACITY = 0.5;

const Marker = ({ color }: { color: string }) => (
  <Box
    sx={{
      width: MARKER_SIZE,
      height: MARKER_SIZE,
      minWidth: MARKER_SIZE,
      minHeight: MARKER_SIZE,
      borderRadius: tokens.theme.radius.xs,
      backgroundColor: color,
    }}
  />
);

const Value = ({
  children,
  color,
  sx,
}: {
  children: ReactNode;
  color?: string;
  sx?: SxProps;
}) => (
  <Typography variant="P12B" color={color} noWrap sx={sx}>
    {children}
  </Typography>
);

const LegendListItem = ({
  item,
  testId,
  divider,
}: {
  item: InternalLegendItem;
  testId?: string;
  divider?: boolean;
}) => {
  const listItem = (
    <InventoryListItem
      testId={testId}
      size="sm"
      divider={divider}
      onClick={item.onClick}
      sx={{
        opacity: item.disabled
          ? DISABLED_OPACITY
          : item.dimmed
          ? DIMMED_OPACITY
          : 1,
      }}
      slots={{
        startContent:
          item.startContent === undefined ? (
            <Marker color={item.markerColor!} />
          ) : (
            item.startContent
          ),
        label: item.label,
        endContent: item.endContent ?? (
          <Value color={item.valueColor} sx={item.valueSx}>
            {item.value}
          </Value>
        ),
      }}
      slotProps={{
        endContent: { variant: 'P12B' },
      }}
    />
  );

  if (item.rowTooltip) {
    return (
      <Tooltip title={item.rowTooltip} placement="top" arrow>
        {listItem}
      </Tooltip>
    );
  }

  return listItem;
};

export const AutoscalingLegend = ({
  testId,
  sx,
  requested,
  requestedMarkerColor = tokens.theme.data.blue300,
  requestedOnClick,
  requestedDisabled,
  requestedValueColor,
  requestedValueSx,
  recommended,
  recommendedMarkerColor = tokens.theme.data.grey900,
  recommendedOnClick,
  recommendedDisabled,
  recommendedValueColor,
  recommendedValueSx,
  recommendedRowTooltip,
  recommendedWarningTooltip,
  recommendedNoChangeTooltip,
  difference,
  differenceDirection = 'down',
  differenceColor,
  metrics,
}: AutoscalingLegendProps) => {
  const DifferenceIcon =
    differenceDirection === 'up' ? Icons.CaretUp : Icons.CaretDown;
  const resolvedDifferenceColor =
    differenceColor ??
    (differenceDirection === 'up'
      ? tokens.theme.fg.status.error
      : tokens.theme.fg.status.success);

  const recommendedEndContent =
    recommendedWarningTooltip || recommendedNoChangeTooltip ? (
      <Stack direction="row" alignItems="center" gap={4}>
        <Value color={recommendedValueColor} sx={recommendedValueSx}>
          {recommended}
        </Value>
        {recommendedWarningTooltip}
        {recommendedNoChangeTooltip}
      </Stack>
    ) : undefined;

  const topGroup: InternalLegendItem[] = [
    {
      label: 'Requested',
      value: requested,
      markerColor: requestedMarkerColor,
      disabled: requestedDisabled,
      onClick: requestedOnClick,
      valueColor: requestedValueColor,
      valueSx: requestedValueSx,
    },
    {
      label: 'Recommended',
      value: recommended,
      markerColor: recommendedMarkerColor,
      disabled: recommendedDisabled,
      onClick: recommendedOnClick,
      valueColor: recommendedValueColor,
      valueSx: recommendedValueSx,
      rowTooltip: recommendedRowTooltip,
      endContent: recommendedEndContent,
    },
    ...(difference
      ? [
          {
            label: 'Difference',
            value: difference,
            startContent: null as ReactNode,
            endContent: (
              <Stack direction="row" gap={4} alignItems="center">
                <DifferenceIcon size={16} color={resolvedDifferenceColor} />
                <Value>{difference}</Value>
              </Stack>
            ),
          },
        ]
      : []),
  ];

  return (
    <Stack gap={16} data-testid={testId} sx={sx}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: tokens.theme.border.structural.neutral,
          borderRadius: tokens.theme.radius.sm,
          overflow: 'hidden',
        }}
      >
        {topGroup.map((item, index) => (
          <LegendListItem
            key={item.label}
            item={item}
            divider={index < topGroup.length - 1}
            testId={
              testId ? `${testId}-${item.label.toLowerCase()}` : undefined
            }
          />
        ))}
      </Box>

      {metrics.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {metrics.map((metric, index) => (
            <LegendListItem
              key={metric.id}
              item={metric}
              divider={index < metrics.length - 1}
              testId={testId ? `${testId}-${metric.id}` : undefined}
            />
          ))}
        </Box>
      )}
    </Stack>
  );
};
